import React, { useState, useEffect, useRef } from "react";
import { useMsal, useAccount } from "@azure/msal-react";
import ConfigSubmissionForm, { ConfigSubmissionPayload } from "../components/ConfigSubmissionForm";
import ApprovalQueue, { PendingRequest } from "../components/ApprovalQueue";
import ConfigAuditLog from "../components/ConfigAuditLog";
import ConfigHealth from "../components/ConfigHealth";
import { SubmitConfigEntry, GetConfigRequestStatus, GetConfigAuditLog, GetPendingConfigRequests, ApproveConfigRequest, RejectConfigRequest, CheckConfigKeys } from "../global/Apis";

type Tab = "submit" | "approvals" | "audit" | "health";
type SubmissionState = "idle" | "submitting" | "polling" | "applied" | "failed" | "timeout" | "pending_approval";



const ConfigStore: React.FC = () => {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const isAdmin = (account?.idTokenClaims as any)?.roles?.includes("Admin") ?? false;
  console.log("idTokenClaims", account?.idTokenClaims);

  const [tab, setTab] = useState<Tab>("submit");
 

  const [submissionState, setSubmissionState] = useState<SubmissionState>("idle");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [statusDetail, setStatusDetail] = useState<any | null>(null);
  

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollAttemptsRef = useRef(0);
  const MAX_POLL_ATTEMPTS = 10;

  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [prefill, setPrefill] = useState<{
    environment?: "development" | "staging" | "production";
    region?: string;
    key?: string;
    bulkInput?: string;
  }>({});
  
  const [existingKeys, setExistingKeys] = useState<string[]>([]);
  const [pendingPayload, setPendingPayload] = useState<any>(null);
  const [formResetKey, setFormResetKey] = useState(0);

  const fetchPendingRequests = async () => {
    if (!account) return;
    try {
      const data = await GetPendingConfigRequests(instance, account);
      setPendingRequests(data);
    } catch (err) {
      console.error("Failed to fetch pending requests", err);
    }
  };

  useEffect(() => {
  if (tab === "approvals" && isAdmin) {
    fetchPendingRequests();
  }
}, [tab, isAdmin]);

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    pollAttemptsRef.current = 0;
  };

  useEffect(() => {
    return () => stopPolling();
  }, []);

  useEffect(() => {
    const handlePrefill = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        keys: string[];
        fromEnv: string;
        toEnv: string;
        region?: string;
      };

      const targetEnv = detail.toEnv as "development" | "staging" | "production";

      if (detail.keys.length === 1) {
        setPrefill({
          environment: targetEnv,
          region: detail.region,
          key: detail.keys[0],
        });
      } else {
        const bulkObject: Record<string, string> = {};
        for (const k of detail.keys) bulkObject[k] = "";
        setPrefill({
          environment: targetEnv,
          region: detail.region,
          bulkInput: JSON.stringify(bulkObject, null, 2),
        });
      }

      setFormResetKey((k) => k + 1);
      setSubmissionState("idle");
      setTab("submit");
    };

    window.addEventListener("opsnest:prefill-config-entry", handlePrefill);
    return () => window.removeEventListener("opsnest:prefill-config-entry", handlePrefill);
  }, []);

  const startPolling = (reqId: string) => {
    pollAttemptsRef.current = 0;

    pollIntervalRef.current = setInterval(async () => {
      pollAttemptsRef.current += 1;

      if (pollAttemptsRef.current > MAX_POLL_ATTEMPTS) {
        stopPolling();
        setSubmissionState("timeout");
        return;
      }

      try {
        const status = await GetConfigRequestStatus(instance, account!, reqId);
        setStatusDetail(status);

        if (status.status === "applied") {
          stopPolling();
          setSubmissionState("applied");
        } else if (status.status === "failed") {
          stopPolling();
          setSubmissionState("failed");
        }
      } catch (err) {
        // don't stop polling on a transient error — just wait for next tick
      }
    }, 3000);
  };

  const handleSubmit = async (payload: ConfigSubmissionPayload) => {
    if (!account) return;
    setSubmissionState("submitting");
    setSubmitError(null);
    setExistingKeys([]);
    setPendingPayload(null);
    stopPolling();

    // Step 1 — check if any keys already exist
    try {
      const keys = payload.entries.map((e) => e.key);
      const { existing_keys } = await CheckConfigKeys(instance, account, {
        environment: payload.environment,
        region: payload.region,
        keys,
      });

      if (existing_keys.length > 0) {
        // Show warning — pause here, don't submit yet
        setExistingKeys(existing_keys);
        setPendingPayload(payload);
        setSubmissionState("idle");
        return;
      }
    } catch {
      // If the check itself fails, proceed with submit anyway
      // rather than blocking the user on a non-critical pre-check
    }

    // Step 2 — no conflicts, submit directly
    await doSubmit(payload);
  };

const handleConfirmSubmit = async () => {
  if (!pendingPayload) return;
  setExistingKeys([]);
  await doSubmit(pendingPayload);
  setPendingPayload(null);
};

const doSubmit = async (payload: ConfigSubmissionPayload) => {
  setSubmissionState("submitting");
  setSubmitError(null);
  try {
    const response = await SubmitConfigEntry(instance, account, {
      environment: payload.environment,
      region: payload.region,
      entries: payload.entries.map((e) => ({
        key: e.key,
        value_type: e.valueType,
        value: e.value,
        vault_uri: e.vaultUri,
        secret_name: e.secretName,
      })),
    });

    setRequestId(response.request_id);
    if (response.result === "pending") {
      setSubmissionState("pending_approval");
    } else {
      setSubmissionState("applied");
      setStatusDetail({ entries: payload.entries });
    }
  } catch (err: any) {
    setSubmitError(err.message || "Submission failed. Please try again.");
    setSubmissionState("failed");
  }
};

  // clear the banner when the user starts typing a new entry

  const handleApprove = async (requestId: string): Promise<void> => {
  if (!account) return;
  try {
    await ApproveConfigRequest(instance, account, requestId);
    await fetchPendingRequests();
  } catch (err: any) {
    console.error("Failed to approve request", err);
  }
};

  const handleReject = async (requestId: string, reason: string) => {
    if (!account) return;
    try {
      await RejectConfigRequest(instance, account, requestId, reason);
      await fetchPendingRequests();
    } catch (err: any) {
      console.error("Failed to reject request", err);
    }
  };

  const renderBanner = () => {
    
    if (existingKeys.length > 0) {
      return (
        <div className="px-3 py-3 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          <div className="flex items-start gap-2 mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59c.75 1.334-.213 2.987-1.743 2.987H3.482c-1.53 0-2.493-1.653-1.743-2.987L8.257 3.1zM11 14a1 1 0 11-2 0 1 1 0 012 0zm-1-2a1 1 0 01-1-1V7a1 1 0 112 0v4a1 1 0 01-1 1z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-medium">
                {existingKeys.length === 1
                  ? "This key already exists in the target store:"
                  : `${existingKeys.length} keys already exist in the target store:`}
              </p>
              <ul className="mt-1 space-y-0.5">
                {existingKeys.map((k) => (
                  <li key={k} className="font-mono text-xs">{k}</li>
                ))}
              </ul>
              <p className="mt-1.5 text-xs text-amber-700">Submitting will overwrite the existing value.</p>
            </div>
          </div>
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={handleConfirmSubmit}
              className="px-3 py-1.5 text-xs font-semibold rounded-md bg-amber-600 text-white hover:bg-amber-700 transition-colors"
            >
              Submit anyway
            </button>
            <button
              type="button"
              onClick={() => { setExistingKeys([]); setPendingPayload(null); }}
              className="px-3 py-1.5 text-xs font-semibold rounded-md border border-amber-300 text-amber-800 hover:bg-amber-100 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    if (submissionState === "idle") return null;

    if (submissionState === "submitting") {
      return (
        <div className="flex items-center px-3 py-2 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-800 text-sm">
          <svg className="animate-spin h-4 w-4 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Submitting...
        </div>
      );
    }

    if (submissionState === "pending_approval") {
      return (
        <div className="flex items-center px-3 py-2 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-800 text-sm">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 1a4 4 0 00-4 4v2H5a1 1 0 00-1 1v8a2 2 0 002 2h8a2 2 0 002-2V8a1 1 0 00-1-1h-1V5a4 4 0 00-4-4zm2 6V5a2 2 0 10-4 0v2h4z" clipRule="evenodd" />
          </svg>
          Submitted for approval. Track the status in the{" "}
          <button
            type="button"
            onClick={() => handleTabChange("audit")}
            className="underline font-medium ml-1 hover:text-indigo-900"
          >
            Audit log
          </button>.
        </div>
      );
    }

    if (submissionState === "polling") {
      return (
        <div className="flex items-center px-3 py-2 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-800 text-sm">
          <svg className="animate-spin h-4 w-4 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Applying to App Config...
        </div>
      );
    }

    if (submissionState === "applied") {
      return (
        <div className="flex items-center px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          <span>
            Successfully applied to App Config
            {statusDetail?.entries?.length === 1 && (
              <span className="ml-1 font-mono">— {statusDetail.entries[0].key}</span>
            )}
            {statusDetail?.entries?.length > 1 && (
              <span className="ml-1">— {statusDetail.entries.length} entries</span>
            )}
          </span>
        </div>
      );
    }

    if (submissionState === "failed") {
      return (
        <div className="flex items-center px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-800 text-sm">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {submitError || "Failed to apply to App Config. Please try again."}
        </div>
      );
    }

    if (submissionState === "timeout") {
      return (
        <div className="flex items-center px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59c.75 1.334-.213 2.987-1.743 2.987H3.482c-1.53 0-2.493-1.653-1.743-2.987L8.257 3.1zM11 14a1 1 0 11-2 0 1 1 0 012 0zm-1-2a1 1 0 01-1-1V7a1 1 0 112 0v4a1 1 0 01-1 1z" clipRule="evenodd" />
          </svg>
          <span>
            Still processing — this is taking longer than expected. Check the{" "}
            <button
              type="button"
              onClick={() => handleTabChange("audit")}
              className="underline font-medium hover:text-amber-900"
            >
              Audit log
            </button>
            {" "}to track your request.
          </span>
        </div>
      );
    }
  };

  const handleTabChange = (newTab: Tab) => {
    if (submissionState === "timeout" || submissionState === "applied" || submissionState === "pending_approval") {
      setSubmissionState("idle");
    }
    setExistingKeys([]);
    setPendingPayload(null);
    setFormResetKey((k) => k + 1);
    setTab(newTab);
  };

  const handleFormChange = () => {
    if (submissionState === "applied" || submissionState === "timeout") {
      setSubmissionState("idle");
    }
    setExistingKeys([]);
    setPendingPayload(null);
  };

  return (
    <div className="container mx-auto px-4 py-6">

      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">App Configuration Management</h1>
        <p className="text-sm text-gray-500 mt-1">Manage App Configuration values across environments</p>
      </div>

      {/* Pill tab bar */}
      <div className="inline-flex bg-white border border-gray-200 rounded-lg p-1 gap-1 mb-6">
        <button
          type="button"
          onClick={() => handleTabChange("submit")}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors duration-150 ${
            tab === "submit"
              ? "bg-indigo-600 text-white"
              : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
          }`}
        >
          New entry
        </button>

        {isAdmin && (
          <button
            type="button"
            onClick={() => handleTabChange("approvals")}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors duration-150 ${
              tab === "approvals"
                ? "bg-indigo-600 text-white"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            Approvals
            {pendingRequests.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-semibold rounded-full bg-rose-100 text-rose-800">
                {pendingRequests.length}
              </span>
            )}
          </button>
        )}

        <button
          type="button"
          onClick={() => handleTabChange("audit")}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors duration-150 ${
            tab === "audit"
              ? "bg-indigo-600 text-white"
              : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
          }`}
        >
          Audit log
        </button>

        <button
          type="button"
          onClick={() => handleTabChange("health")}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors duration-150 ${
            tab === "health"
              ? "bg-indigo-600 text-white"
              : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
          }`}
        >
          Config health
        </button>
      </div>

      {/* Tab content */}
      {tab === "submit" && (
        <div className="space-y-3">
          {renderBanner()}
          <ConfigSubmissionForm
            key={formResetKey}
            onSubmit={handleSubmit}
            isSubmitting={submissionState === "submitting" || submissionState === "polling"}
            submissionState={submissionState}
            onChange={handleFormChange}
            initialEnvironment={prefill.environment}
            initialRegion={prefill.region}
            initialKey={prefill.key}
            initialBulkInput={prefill.bulkInput}
          />
        </div>
    )}
      
      {tab === "approvals" && isAdmin && (
        <ApprovalQueue
          requests={pendingRequests}
          onApprove={handleApprove}
          onReject={handleReject}
          onRefresh={fetchPendingRequests}
        />
      )}    

      {tab === "audit" && <ConfigAuditLog />}
      {tab === "health" && <ConfigHealth />}
    </div>
  );
};

export default ConfigStore;